import React, { useState, useEffect } from 'react';
import { BellRing } from 'lucide-react';
import { Switch } from '../ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ReminderSettings() {
  const [remindersEnabled, setRemindersEnabled] = useState(true);
  const [daysBefore, setDaysBefore] = useState(1);
  const [approvalRequired, setApprovalRequired] = useState(true);
  const [offerExpiryDays, setOfferExpiryDays] = useState(7);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/settings`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) {
          setRemindersEnabled(data.reminders_enabled ?? true);
          setDaysBefore(data.reminder_days_before ?? 1);
          setApprovalRequired(data.booking_approval_required ?? true);
          setOfferExpiryDays(data.offer_expiry_days ?? 7);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async (patch) => {
    try {
      await fetch(`${API}/settings`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(patch),
      });
    } catch (e) {
      console.error('Failed to save reminder settings:', e);
    }
  };

  if (loading) return null;

  return (
    <div className="bg-surface rounded-lg border border-border p-6 mb-4" data-testid="reminder-settings-card">
      <div className="flex items-center gap-2 mb-4">
        <BellRing className="w-5 h-5 text-primary" />
        <h2 className="text-lg font-medium text-foreground">Bookings & Reminders</h2>
      </div>

      <div className="space-y-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-foreground">Require approval for booking requests</div>
            <div className="text-xs text-text-muted mt-0.5">
              New public bookings wait for your accept/decline before landing on your schedule
            </div>
          </div>
          <Switch
            checked={approvalRequired}
            onCheckedChange={(v) => { setApprovalRequired(v); save({ booking_approval_required: v }); }}
            data-testid="approval-required-toggle"
          />
        </div>

        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm font-medium text-foreground">Client session reminders</div>
            <div className="text-xs text-text-muted mt-0.5">
              Automatically email clients a reminder before their session
            </div>
          </div>
          <Switch
            checked={remindersEnabled}
            onCheckedChange={(v) => { setRemindersEnabled(v); save({ reminders_enabled: v }); }}
            data-testid="reminders-enabled-toggle"
          />
        </div>

        {remindersEnabled && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1.5">Send reminder</label>
            <select
              value={daysBefore}
              onChange={(e) => { const v = Number(e.target.value); setDaysBefore(v); save({ reminder_days_before: v }); }}
              className="w-full bg-surface-raised border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:border-primary outline-none"
              data-testid="reminder-days-select"
            >
              <option value={1}>1 day before</option>
              <option value={2}>2 days before</option>
              <option value={3}>3 days before</option>
              <option value={5}>5 days before</option>
              <option value={7}>1 week before</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-foreground mb-1.5">Reschedule offers expire after</label>
          <p className="text-xs text-text-muted mb-1.5">
            One-click rebook links stop working after this many days
          </p>
          <select
            value={offerExpiryDays}
            onChange={(e) => { const v = Number(e.target.value); setOfferExpiryDays(v); save({ offer_expiry_days: v }); }}
            className="w-full bg-surface-raised border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:border-primary outline-none"
            data-testid="offer-expiry-select"
          >
            <option value={3}>3 days</option>
            <option value={5}>5 days</option>
            <option value={7}>7 days</option>
            <option value={14}>14 days</option>
            <option value={30}>30 days</option>
          </select>
        </div>
      </div>
    </div>
  );
}
